import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import GenerationPanel from './components/GenerationPanel';
import PromptInterface from './components/PromptInterface';
import ContentEditor from './components/ContentEditor';
import TemplateSelector from './components/TemplateSelector';
import DataSourceSelector from './components/DataSourceSelector';
import ExportPanel from './components/ExportPanel';
import VersionHistory from './components/VersionHistory';

const AIReportAssistant = () => {
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const tabs = [
    { id: 'generate', label: 'AI Generation', icon: 'Sparkles' },
    { id: 'templates', label: 'Templates', icon: 'FileText' },
    { id: 'editor', label: 'Content Editor', icon: 'Edit' },
    { id: 'data', label: 'Data Sources', icon: 'Database' },
    { id: 'export', label: 'Export', icon: 'Download' },
    { id: 'history', label: 'Version History', icon: 'Clock' },
  ];

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Sparkles" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">AI Report Assistant</h1>
              <p className="text-muted-foreground">
                Intelligent text generation and narrative creation for safety reports
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
            <Icon name="Zap" size={20} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">AI Powered</span>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm mb-6">
        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'generate' && (
            <div className="space-y-6">
              <PromptInterface
                onGenerate={(content) => {
                  setGeneratedContent(content);
                  setActiveTab('editor');
                }}
                selectedTemplate={selectedTemplate}
                selectedDataSources={selectedDataSources}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
              />
              <GenerationPanel
                content={generatedContent}
                isGenerating={isGenerating}
              />
            </div>
          )}

          {activeTab === 'templates' && (
            <TemplateSelector
              selectedTemplate={selectedTemplate}
              onSelectTemplate={setSelectedTemplate}
              onUseTemplate={(template) => {
                setSelectedTemplate(template);
                setActiveTab('generate');
              }}
            />
          )}

          {activeTab === 'editor' && (
            <ContentEditor
              content={generatedContent}
              onContentChange={setGeneratedContent}
            />
          )}

          {activeTab === 'data' && (
            <DataSourceSelector
              selectedDataSources={selectedDataSources}
              onSourcesChange={setSelectedDataSources}
            />
          )}

          {activeTab === 'export' && (
            <ExportPanel
              content={generatedContent}
            />
          )}

          {activeTab === 'history' && (
            <VersionHistory
              onRestoreVersion={(content) => {
                setGeneratedContent(content);
                setActiveTab('editor');
              }}
            />
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">AI-Powered Report Generation</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Use natural language prompts to generate executive summaries, trend analyses, compliance statements, and recommendations. 
              The AI analyzes your selected data sources and maintains regulatory compliance throughout the generation process.
            </p>
            <div className="flex flex-wrap gap-3">
              <button 
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                onClick={() => setActiveTab('templates')}
              >
                <Icon name="FileText" size={16} />
                Browse Templates
              </button>
              <button 
                className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium"
                onClick={() => setActiveTab('data')}
              >
                <Icon name="Database" size={16} />
                Configure Data Sources
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReportAssistant;